import { CurrencyDollarIcon } from "@heroicons/react/24/solid";
import React from "react";
import { Button } from "@/core/components/ui/button";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import { formatDate } from "@/core/utils/date";
import { formatMoneyThousands } from "@/core/utils/money";
import { getCustomerNameOrPhone } from "@/customer/utils";
import { useGetMyUser } from "@/user/query";
import { isAdminOrOwnerOrManager } from "@/user/utils";
import InfoPair from "./info-pair";
import { NameAndDate, NameAndDateSkeleton } from "./name-and-date";
import type { ReferralContentProps } from "./referralContent";

export const ClosedReferralCardSkeleton = () => (
  <Stack className="p-4" fullWidth>
    <NameAndDateSkeleton />
    <Stack gap="sm" fullWidth>
      <Skeleton className="h-4 w-32" /> {/* Service Date */}
      <Skeleton className="h-4 w-48" /> {/* Referred by */}
      <Stack direction="row" align="center" className="gap-1">
        <Skeleton className="h-4 w-4 rounded-full" /> {/* Dollar icon */}
        <Skeleton className="h-4 w-12" /> {/* Amount */}
      </Stack>
    </Stack>
    <Skeleton className="h-9 w-full rounded-full" /> {/* Pay button */}
  </Stack>
);

export interface ClosedReferralCardContentProps extends ReferralContentProps {
  onPayout: () => void;
  onLost?: () => void;
  onMoveToPaid?: () => void;
  onDelete?: () => void;
}

export const ClosedReferralCardContent = React.memo(
  ({
    referral,
    onPayout,
    onLost,
    onMoveToPaid,
    onDelete,
  }: ClosedReferralCardContentProps) => {
    const { data: userInfo } = useGetMyUser();
    const canPayout = isAdminOrOwnerOrManager(userInfo?.role);
    return (
      <Stack className="p-4" fullWidth>
        <NameAndDate
          referral={referral}
          hideDate
          onLost={onLost}
          onMoveToPaid={onMoveToPaid}
          onDelete={onDelete}
        />
        <Stack gap="sm" fullWidth>
          <InfoPair
            label="Service Date"
            info={formatDate(referral.appointment_date)}
          />
          <InfoPair
            label="Referred by"
            info={getCustomerNameOrPhone(referral.advocate)}
          />
          <Stack direction="row" align="center" className="gap-1">
            <CurrencyDollarIcon className="h-4 w-4 text-tertiary-text" />
            <Typography variant="captionW1" textColor="secondary">
              {formatMoneyThousands(referral.deal_size) || "No ticket size"}
            </Typography>
          </Stack>
        </Stack>
        <Tooltip delayDuration={canPayout ? 10000 : 750}>
          <TooltipTrigger className="w-full" asDiv>
            <Button
              size="sm"
              fullWidth
              disabled={!canPayout}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!canPayout) {
                  return;
                }
                onPayout();
              }}
            >
              Pay
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Only owners and managers can pay advocates for referrals.
          </TooltipContent>
        </Tooltip>
      </Stack>
    );
  },
);
